import call from "../lib/core/functional/call";
import curry from "../lib/core/functional/curry";
import thunk from "../lib/core/functional/thunk";

const FLEXIBLE_CLASS = "rm-top-sticky-flexible";
const STUCK_CLASS = "rm-top-sticky-stuck";
const PINNED_AREA_CLASS = "rm-top-sticky-pinned-area";
const SMALL_HEADER_CLASS = "with-small-header";
const PINNED_ONLY_CLASS = "rm-pinned-only";
const PINNED_AREA_SELECTOR = `.${PINNED_AREA_CLASS}`;
const PINNED_AREA_FLOATER_CLASS = "rm-top-sticky-floater";
const MINIMUM_VISIBILITY = 0;
const DEFAULT_VISIBILITY = "all";

function createStylesheet(elId) {
    const styleElement = document.createElement("style");
    styleElement.id = elId;
    document.head.appendChild(styleElement);
    return styleElement;
}

function composeAndApplyCSS(elId, totalHeight, heightToHide) {
    const styleSheetId = `stiky-style-${elId}`;
    const stylesheet =
        document.querySelector(`style#${styleSheetId}`) || createStylesheet(styleSheetId);
    stylesheet.textContent = `
    .can-hide-${elId} #${elId}.rm-top-sticky-flexible.rm-pinned-only{
        transform: translate3d(0px, -${totalHeight}px, 0px);
    }

    .can-hide-${elId} #${elId}.rm-top-sticky-flexible.rm-pinned-only .rm-top-sticky-pinned-area{
        transform: translate3d(0px, ${heightToHide}px, 0px);
    }

    #trigger-${elId}{
        position: absolute;
        top: 0px;
        left: 0px;
        transform: translate3d(0px, ${totalHeight}px, 0px);
    }`;
}

function addTrigger(stickyId, container, className) {
    const triggerElement = document.createElement("span");
    triggerElement.id = `trigger-${stickyId}`;
    triggerElement.dataset.target = `#${stickyId}`;
    triggerElement.dataset.toggleClass = className;
    triggerElement.dataset.triggers = "classChange";
    triggerElement.className = "js-scroll-trigger";
    container.appendChild(triggerElement);
}

function resizeListenerFactory() {
    const callbacks = [];
    const callCallbacks = call();

    function runCallbacks() {
        callbacks.forEach(callCallbacks);
    }

    function registerCallback(fun) {
        callbacks.push(fun);
        if (callbacks.length === 1) {
            window.addEventListener("resize", runCallbacks, { passive: true });
        }
    }

    return {
        registerCallback,
    };
}

function visibilityHandlerFactory() {
    const handlers = {};

    const allowHide = thunk(function allowHide(className, stickyId) {
        handlers[stickyId].timeoutId = null;
        document.body.classList.add(className);
    });

    const showElement = thunk(function showElement(
        className,
        stickyId,
        visibilityTime,
        allowHideCallback
    ) {
        document.body.classList.remove(className);
        if (handlers[stickyId].timeoutId) {
            clearTimeout(handlers[stickyId].timeoutId);
        }
        handlers[stickyId].timeoutId = setTimeout(allowHideCallback, visibilityTime);
    });

    function initiate(stickyElement) {
        const stickyId = stickyElement.id;
        const visibilityTime =
            "minimumVisibility" in stickyElement.dataset
                ? parseInt(stickyElement.dataset.minimumVisibility, 10)
                : MINIMUM_VISIBILITY;
        const initialVisibility =
            "initialVisibility" in stickyElement.dataset
                ? stickyElement.dataset.initialVisibility
                : DEFAULT_VISIBILITY;

        const className = `can-hide-${stickyId}`;
        const allowHideCallback = allowHide(className, stickyId);

        const handler = {
            timeoutId: null,
            show: showElement(className, stickyId, visibilityTime, allowHideCallback),
        };

        handlers[stickyId] = handler;

        if (initialVisibility === "pinned") {
            stickyElement.classList.add(PINNED_ONLY_CLASS);
            document.body.classList.add(className);
        } else {
            if (visibilityTime === 0) {
                document.body.classList.add(className);
            } else {
                handlers[stickyId].timeoutId = setTimeout(allowHideCallback, visibilityTime);
            }
        }
    }

    function show(stickyElement) {
        if (!stickyElement.classList.contains(FLEXIBLE_CLASS)) {
            return null;
        }
        const stickyId = stickyElement.id;
        if (stickyId in handlers) {
            handlers[stickyId].show();
        }
    }

    return {
        initiate,
        show,
    };
}

function setUpFlexibleSticky(stickyElement, pinnedArea) {
    const stickyId = stickyElement.id;
    return function calculateDimensions() {
        const { height: pinnedHeight, bottom: pinnedBottom } = pinnedArea.getBoundingClientRect();
        const {
            height: totalHeight,
            bottom: containerBottom,
        } = stickyElement.getBoundingClientRect();
        const flexibleHeight = pinnedHeight + Math.abs(containerBottom - pinnedBottom);
        composeAndApplyCSS(stickyId, totalHeight, flexibleHeight);
    };
}

function handleStuckDoubleSticky(stickyElement) {
    const pinnedArea = stickyElement.querySelector(PINNED_AREA_SELECTOR);
    if (!pinnedArea) {
        return null;
    }

    const floater = pinnedArea.cloneNode(true);
    floater.id = `${PINNED_AREA_FLOATER_CLASS}-${floater.id.split("-").pop()}`;
    floater.classList.remove(PINNED_AREA_CLASS);
    floater.classList.add(PINNED_AREA_FLOATER_CLASS);
    floater.classList.add(SMALL_HEADER_CLASS);
    pinnedArea.insertAdjacentElement("afterend", floater);
    addTrigger(stickyElement.id, stickyElement, "rm-stuck");
}

const handleFlexibleDoubleSticky = curry(function handleFlexibleDoubleSticky(
    resizeListener,
    visibilityHandler,
    stickyElement
) {
    const pinnedArea = stickyElement.querySelector(PINNED_AREA_SELECTOR);
    if (!pinnedArea) {
        return null;
    }
    const calculator = setUpFlexibleSticky(stickyElement, pinnedArea);
    visibilityHandler.initiate(stickyElement);
    resizeListener.registerCallback(calculator);
    addTrigger(stickyElement.id, document.body, "rm-pinned-only");
    calculator();
});

const resizeListener = resizeListenerFactory();
const visibilityHandler = visibilityHandlerFactory();

export const showFlexibleSticky = visibilityHandler.show;

export default function doubleStickyModule(_context, stickyElement) {
    const handler = stickyElement.classList.contains(STUCK_CLASS)
        ? handleStuckDoubleSticky
        : handleFlexibleDoubleSticky(resizeListener, visibilityHandler);
    handler(stickyElement);
}
